import React, { useCallback, useEffect, useMemo, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { organizationsService, usersService } from "../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const groups = [
  { key: "owner", label: "Sahip" },
  { key: "staff", label: "Personel" },
  { key: "customer", label: "Müşteri" },
];

const OrganizationAuthorizationsPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const [group, setGroup] = useState("owner");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", phone: "" });
  const [phoneCheck, setPhoneCheck] = useState(null);
  const [checkingPhone, setCheckingPhone] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await usersService.getAllOrganizations();
      if (res.success) {
        const list = res.data || [];
        setOrganizations(list);
        if (list.length) {
          setOrganizationId(String(list[0].id));
        }
      } else {
        toast.error(res.error || "Organizasyonlar alınamadı");
      }
    })();
  }, []);

  const selectedOrg = useMemo(
    () => organizations.find((o) => String(o.id) === String(organizationId)),
    [organizations, organizationId]
  );

  const loadAuthorizations = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    const res = await organizationsService.getOrganizationAuthorizations(organizationId, {
      group,
      search,
      page: pagination.page,
      limit: pagination.limit,
    });
    setLoading(false);
    if (res.success) {
      setUsers(res.data || []);
      setPagination((prev) => ({
        ...prev,
        total: res.pagination?.total || 0,
        totalPages: res.pagination?.totalPages || 0,
      }));
    } else {
      toast.error(res.error || "Yetkilendirmeler alınamadı");
    }
  }, [organizationId, group, search, pagination.page, pagination.limit]);

  useEffect(() => {
    loadAuthorizations();
  }, [loadAuthorizations]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPagination((p) => ({ ...p, page: 1 }));
    loadAuthorizations();
  };

  const checkPhone = async () => {
    if (!organizationId || !createForm.phone.trim()) return;
    setCheckingPhone(true);
    const res = await organizationsService.checkAuthorizationPhone(
      organizationId,
      createForm.phone,
      group
    );
    setCheckingPhone(false);
    if (!res.success) {
      setPhoneCheck({ ok: false, text: res.error || "Telefon kontrolü yapılamadı" });
      return;
    }
    if (!res.exists) {
      setPhoneCheck({ ok: true, text: "Bu telefon yeni kullanıcı olarak eklenebilir." });
      return;
    }
    if (res.canAdd) {
      const userName = res.user?.name || "Mevcut kullanıcı";
      setPhoneCheck({ ok: true, text: `${userName} bulundu, yetki verilebilir.` });
    } else {
      setPhoneCheck({ ok: false, text: res.reason || "Bu telefon zaten kayıtlı." });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!organizationId) return toast.error("Önce organizasyon seçiniz");
    if (!createForm.name.trim() || !createForm.phone.trim()) {
      return toast.error("Ad soyad ve telefon zorunludur");
    }
    setSaving(true);
    const res = await organizationsService.createOrganizationAuthorization(organizationId, {
      group,
      name: createForm.name.trim(),
      phone: createForm.phone.trim(),
    });
    setSaving(false);
    if (res.success) {
      toast.success(res.message || "Yetki eklendi");
      setCreateForm({ name: "", phone: "" });
      setPhoneCheck(null);
      loadAuthorizations();
    } else {
      toast.error(res.error || "Yetki eklenemedi");
    }
  };

  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title="Organizasyon Yetkilendirmeler" />
      <div className="dashboard-main active">
        <div className="container-fluid">
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-lg-5">
                  <label className="form-label">Organizasyon</label>
                  <select
                    className="form-select"
                    value={organizationId}
                    onChange={(e) => {
                      setOrganizationId(e.target.value);
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                  >
                    {organizations.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name || o.title} (#{o.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-7">
                  <ul className="nav nav-pills gap-2 flex-wrap">
                    {groups.map((g) => (
                      <li className="nav-item" key={g.key}>
                        <button
                          type="button"
                          className={`btn ${group === g.key ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => {
                            setGroup(g.key);
                            setPagination((p) => ({ ...p, page: 1 }));
                            setPhoneCheck(null);
                          }}
                        >
                          {g.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="small text-muted mt-2">
                Seçili: <strong>{selectedOrg?.name || selectedOrg?.title || "-"}</strong>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-3">Yeni ekle</h6>
              <form onSubmit={handleCreate} className="row g-2">
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Ad Soyad"
                    value={createForm.name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Telefon"
                    value={createForm.phone}
                    onBlur={checkPhone}
                    onChange={(e) => {
                      setCreateForm((f) => ({ ...f, phone: e.target.value }));
                      setPhoneCheck(null);
                    }}
                  />
                </div>
                <div className="col-md-4 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={checkPhone}
                    disabled={checkingPhone || !organizationId}
                  >
                    {checkingPhone ? "Kontrol..." : "Numarayı Kontrol Et"}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving || !organizationId}>
                    {saving ? "Ekleniyor..." : "Yeni Ekle"}
                  </button>
                </div>
              </form>
              {phoneCheck && (
                <div className={`mt-2 small ${phoneCheck.ok ? "text-success" : "text-danger"}`}>
                  {phoneCheck.text}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header bg-base border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
              <form className="d-flex gap-2" onSubmit={onSearchSubmit}>
                <input
                  className="form-control"
                  placeholder="Ad / telefon ara"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-primary" type="submit">
                  Ara
                </button>
              </form>
              <select
                className="form-select w-auto"
                value={pagination.limit}
                onChange={(e) => setPagination((p) => ({ ...p, limit: Number(e.target.value), page: 1 }))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="p-4 text-center text-muted">Yükleniyor...</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Ad Soyad</th>
                        <th>Telefon</th>
                        <th>Email</th>
                        <th>Rol</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            Kayıt bulunamadı
                          </td>
                        </tr>
                      )}
                      {users.map((u, i) => (
                        <tr key={`${u.id}-${i}`}>
                          <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                          <td>{u.name}</td>
                          <td>{u.phone || "-"}</td>
                          <td>{u.email || "-"}</td>
                          <td>{u.role_name || (group === "owner" ? "Sahip" : group === "staff" ? "Personel" : "Müşteri")}</td>
                          <td>
                            {Number(u.active) === 1 ? (
                              <span className="badge bg-success">Aktif</span>
                            ) : (
                              <span className="badge bg-secondary">Pasif</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card-footer d-flex justify-content-between align-items-center">
              <span className="small text-muted">
                Toplam: {pagination.total}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                >
                  Önceki
                </button>
                <span className="small align-self-center">
                  {pagination.page} / {pagination.totalPages || 1}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pagination.page >= (pagination.totalPages || 1)}
                  onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default OrganizationAuthorizationsPage;
